<template>
  <div class="pa-3 inventory-scan-map-page">
    <v-card outlined class="mb-3">
      <v-card-text class="py-3">
        <div class="d-flex justify-space-between align-center flex-wrap">
          <div>
            <div class="subtitle-1 font-weight-bold">Inventory scan map</div>
            <div class="caption grey--text text--darken-1">
              Matched vs unmatched submissions by GPS. Totals reflect all rows; pins may be capped by
              limit.
            </div>
          </div>
          <div class="d-flex align-center flex-wrap mt-2 mt-md-0">
            <v-btn small text class="mr-1" :to="{ name: 'MobileV2OuternetHeatmap' }">
              <v-icon left small>mdi-arrow-left</v-icon>
              Back to heatmap
            </v-btn>
            <v-btn
              small
              color="primary"
              outlined
              :loading="loadState === 'loading'"
              :disabled="loadState === 'loading'"
              @click="loadMarkers()"
            >
              Refresh
            </v-btn>
          </div>
        </div>

        <v-row dense class="mt-3">
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile" outlined rounded>
              <div class="caption grey--text text--darken-1">Total (dataset)</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(totals.count) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile kpi-tile--matched" outlined rounded>
              <div class="caption">Matched</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(totals.matched) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile kpi-tile--unmatched" outlined rounded>
              <div class="caption">Unmatched</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(totals.unmatched) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3" v-if="serverLimit != null">
            <v-sheet class="kpi-tile" outlined rounded>
              <div class="caption grey--text text--darken-1">Pin cap (limit)</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(serverLimit) }}</div>
            </v-sheet>
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-3">
          <v-switch v-model="showMatched" class="mt-0 mr-4" hide-details label="Show matched" dense />
          <v-switch v-model="showUnmatched" class="mt-0 mr-4" hide-details label="Show unmatched" dense />
          <v-switch v-model="isSatellite" class="mt-0" hide-details label="Satellite" dense />
          <v-spacer class="d-none d-sm-block" />
          <v-btn small outlined class="mt-2 mt-sm-0" @click="fitVisibleMarkers">
            Fit map
          </v-btn>
        </div>
        <div v-if="markers.length && markersShown < markers.length" class="caption grey--text mt-2">
          Showing {{ formatNum(markersShown) }} of {{ formatNum(markers.length) }} loaded markers
          (performance cap).
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-if="loadState === 'error'" type="error" dense outlined class="mb-3">
      {{ errorMessage }}
    </v-alert>

    <v-alert v-else-if="loadState === 'ok' && !markers.length" type="info" dense outlined class="mb-3">
      No markers returned for this view.
    </v-alert>

    <div ref="mapEl" class="map-canvas" />

    <div class="legend-row d-flex flex-wrap align-center mt-3 caption">
      <span class="font-weight-medium mr-3">Legend</span>
      <span class="legend-dot legend-dot--matched mr-1" />
      <span class="mr-4">Matched (zoomed in)</span>
      <span class="legend-dot legend-dot--unmatched mr-1" />
      <span class="mr-4">Unmatched (zoomed in)</span>
      <span class="legend-pie legend-pie--sample mr-1" aria-hidden="true" />
      <span>Clusters: pie = matched vs unmatched in that area; number = total</span>
    </div>
  </div>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2.js";
import { MarkerClusterer, SuperClusterViewportAlgorithm } from "@googlemaps/markerclusterer";

const service = new RestResource();
const DEFAULT_MARKER_LIMIT = 1500;
const CLIENT_MARKER_CAP = 1200;
/** Build markers in slices so the main thread stays responsive during load. */
const MARKER_BUILD_CHUNK = 180;
const META_KEY = "izcInventoryPoint";
const MATCH_HEX = "#00897B";
const UNMATCH_HEX = "#E53935";

function countMatchedInCluster(cluster) {
  let matched = 0;
  let unmatched = 0;
  cluster.markers.forEach(marker => {
    const p = marker.get(META_KEY);
    if (!p) {
      return;
    }
    if (p.isInventoryMatched) {
      matched += 1;
    } else {
      unmatched += 1;
    }
  });
  return { matched, unmatched };
}

/** Clockwise pie slice from startDeg to endDeg (degrees, 0 = east, SVG y-down). */
function pieSlicePath(cx, cy, r, startDeg, endDeg) {
  if (endDeg - startDeg < 0.05) {
    return "";
  }
  const t0 = (startDeg * Math.PI) / 180;
  const t1 = (endDeg * Math.PI) / 180;
  const x0 = cx + r * Math.cos(t0);
  const y0 = cy + r * Math.sin(t0);
  const x1 = cx + r * Math.cos(t1);
  const y1 = cy + r * Math.sin(t1);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} Z`;
}

function buildClusterSvgDataUrl(total, matched, unmatched) {
  const cx = 120;
  const cy = 120;
  const r = 92;
  const parts = [];
  if (total < 1) {
    parts.push(
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#b0bec5" stroke="#fff" stroke-width="4"/>`
    );
  } else if (unmatched === 0) {
    parts.push(
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${MATCH_HEX}" stroke="#fff" stroke-width="4"/>`
    );
  } else if (matched === 0) {
    parts.push(
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${UNMATCH_HEX}" stroke="#fff" stroke-width="4"/>`
    );
  } else {
    const matchedSweep = (360 * matched) / total;
    const a0 = -90;
    const a1 = a0 + matchedSweep;
    const a2 = a0 + 360;
    const pMatch = pieSlicePath(cx, cy, r, a0, a1);
    const pUnmatch = pieSlicePath(cx, cy, r, a1, a2);
    if (pMatch) {
      parts.push(`<path d="${pMatch}" fill="${MATCH_HEX}" stroke="#fff" stroke-width="3"/>`);
    }
    if (pUnmatch) {
      parts.push(`<path d="${pUnmatch}" fill="${UNMATCH_HEX}" stroke="#fff" stroke-width="3"/>`);
    }
  }

  const innerR = 54;
  parts.push(`<circle cx="${cx}" cy="${cy}" r="${innerR}" fill="#fafafa" stroke="#eceff1" stroke-width="2"/>`);

  const label =
    total > 999
      ? `${Math.round(total / 1000)}k`
      : total > 99
        ? String(total)
        : String(total);
  const subSize = total > 99 ? 18 : 22;
  const mainSize = total > 99 ? 34 : 40;
  parts.push(
    `<text x="${cx}" y="${cy + 6}" text-anchor="middle" font-family="Roboto,system-ui,sans-serif" font-size="${mainSize}" font-weight="700" fill="#263238">${label}</text>`,
    `<text x="${cx}" y="${cy + 34}" text-anchor="middle" font-family="Roboto,system-ui,sans-serif" font-size="${subSize}" font-weight="600">`,
    `<tspan fill="${MATCH_HEX}">M${matched}</tspan><tspan fill="#90a4ae"> · </tspan><tspan fill="${UNMATCH_HEX}">U${unmatched}</tspan>`,
    `</text>`
  );

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">${parts.join("")}</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/** Encodes matched vs unmatched in cluster icons (replaces default blue/red size heuristic). */
const inventoryClusterRenderer = {
  render(cluster) {
    const google = window.google;
    const { matched, unmatched } = countMatchedInCluster(cluster);
    const total = cluster.count;
    const url = buildClusterSvgDataUrl(total, matched, unmatched);
    const size = 56;
    const half = size / 2;
    return new google.maps.Marker({
      position: cluster.position,
      icon: {
        url,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(half, half),
      },
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + total,
      title: `${total} in area — ${matched} matched, ${unmatched} unmatched`,
    });
  },
};

export default {
  name: "InventoryScanMapPage",

  data() {
    return {
      loadState: "idle",
      errorMessage: "",
      markers: [],
      totals: { count: 0, matched: 0, unmatched: 0 },
      serverLimit: null,
      map: null,
      gMarkers: [],
      markerClusterer: null,
      markerBuildRafId: null,
      infoWindow: null,
      showMatched: true,
      showUnmatched: true,
      isSatellite: false,
    };
  },

  computed: {
    markersShown() {
      return Math.min(this.markers.length, CLIENT_MARKER_CAP);
    },
  },

  watch: {
    showMatched() {
      this.renderMarkers();
    },
    showUnmatched() {
      this.renderMarkers();
    },
    isSatellite(v) {
      if (this.map) {
        this.map.setMapTypeId(v ? "hybrid" : "roadmap");
      }
    },
  },

  async mounted() {
    await this.loadGoogleMapsScript();
    this.initMap();
    await this.loadMarkers(false);
  },

  beforeDestroy() {
    this.clearGoogleMarkers();
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow = null;
    }
    this.map = null;
  },

  methods: {
    formatNum(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "—";
      }
      return n.toLocaleString();
    },

    parseApiResponse(res) {
      const body = (res && res.data) || {};
      const inner = body.data && typeof body.data === "object" ? body.data : body;
      const rawMarkers = Array.isArray(inner.markers) ? inner.markers : [];
      const t = inner.totals && typeof inner.totals === "object" ? inner.totals : {};
      const count = Number(t.count);
      const matched = Number(t.matched);
      const unmatched = Number(t.unmatched);
      return {
        markers: rawMarkers
          .map(m => ({
            _id: m._id,
            lat: Number(m.lat),
            lng: Number(m.lng),
            isInventoryMatched: Boolean(m.isInventoryMatched),
            inventoryScanStatus: m.inventoryScanStatus || "",
            imageUrl: m.imageUrl || "",
            brand: m.brand || "",
            address: m.address || "",
            city: m.city || "",
            state: m.state || "",
          }))
          .filter(
            m =>
              m._id &&
              Number.isFinite(m.lat) &&
              Number.isFinite(m.lng) &&
              Math.abs(m.lat) <= 90 &&
              Math.abs(m.lng) <= 180
          ),
        totals: {
          count: Number.isFinite(count) ? count : 0,
          matched: Number.isFinite(matched) ? matched : 0,
          unmatched: Number.isFinite(unmatched) ? unmatched : 0,
        },
        limit: inner.limit != null ? Number(inner.limit) : null,
      };
    },

    safeHttpsUrl(url) {
      if (typeof url !== "string" || !url) {
        return "";
      }
      try {
        const u = new URL(url);
        if (u.protocol !== "https:" && u.protocol !== "http:") {
          return "";
        }
        return url;
      } catch {
        return "";
      }
    },

    async loadMarkers() {
      this.loadState = "loading";
      this.errorMessage = "";
      try {
        const res = await service.getInventoryComparision({ limit: DEFAULT_MARKER_LIMIT });
        const parsed = this.parseApiResponse(res);
        this.markers = parsed.markers;
        this.totals = parsed.totals;
        this.serverLimit = parsed.limit;
        this.loadState = "ok";
        this.$nextTick(() => {
          this.renderMarkers();
          this.fitVisibleMarkers();
        });
      } catch (err) {
        this.markers = [];
        this.loadState = "error";
        this.errorMessage =
          (err.response && err.response.data && err.response.data.error) ||
          err.message ||
          "Failed to load inventory map markers.";
      }
    },

    loadGoogleMapsScript() {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const existingScript = document.getElementById("google-maps-script");
        if (existingScript) {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener(
            "error",
            () => reject(new Error("Failed to load Google Maps script")),
            { once: true }
          );
          return;
        }
        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAP}&v=weekly&libraries=visualization`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Google Maps script"));
        document.head.appendChild(script);
      });
    },

    initMap() {
      const { google } = window;
      this.map = new google.maps.Map(this.$refs.mapEl, {
        zoom: 6,
        center: { lat: 4.2, lng: 108.0 },
        mapTypeId: "roadmap",
        tilt: 0,
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: false,
        keyboardShortcuts: false,
      });
      this.infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
    },

    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    visibleMarkerList() {
      const slice = this.markers.slice(0, CLIENT_MARKER_CAP);
      return slice.filter(m => {
        if (m.isInventoryMatched) {
          return this.showMatched;
        }
        return this.showUnmatched;
      });
    },

    cancelMarkerBuild() {
      if (this.markerBuildRafId != null) {
        cancelAnimationFrame(this.markerBuildRafId);
        this.markerBuildRafId = null;
      }
    },

    disposeMarkerClusterer() {
      if (this.markerClusterer) {
        this.markerClusterer.setMap(null);
        this.markerClusterer = null;
      }
    },

    clearGoogleMarkers() {
      this.cancelMarkerBuild();
      this.disposeMarkerClusterer();
      this.gMarkers.forEach(marker => marker.setMap(null));
      this.gMarkers = [];
    },

    onInventoryMarkerClick(marker) {
      if (!this.infoWindow || !marker) {
        return;
      }
      const point = marker.get(META_KEY);
      if (!point) {
        return;
      }
      const matched = point.isInventoryMatched;
      const matchedColor = "#00897B";
      const unmatchedColor = "#E53935";
      const brand = this.escapeHtml(point.brand || "—");
      const statusLabel = matched ? "Matched" : "Unmatched";
      const rawStatus = this.escapeHtml(point.inventoryScanStatus || "—");
      const addr = this.escapeHtml(point.address || [point.city, point.state].filter(Boolean).join(", ") || "—");
      const imgSrc = this.safeHttpsUrl(point.imageUrl);
      const loadedCount = this.markersShown;
      const thumb =
        imgSrc && loadedCount <= 80
          ? `<div style="margin-top:8px;"><img src="${imgSrc.replace(/"/g, "")}" alt="Billboard" style="max-width:220px;max-height:140px;border-radius:8px;object-fit:cover;" /></div>`
          : imgSrc
            ? `<div class="caption grey--text" style="margin-top:6px;">Photo omitted (large set). Open submission for full media.</div>`
            : "";

      this.infoWindow.setContent(
        `<div style="min-width:180px;max-width:280px;font-size:13px;line-height:1.45;">
          <strong>${brand}</strong><br/>
          <span style="color:${matched ? matchedColor : unmatchedColor};font-weight:600;">${statusLabel}</span>
          <span style="color:#78909c;"> (${rawStatus})</span><br/>
          <span style="color:#455a64;">${addr}</span>
          ${thumb}
        </div>`
      );
      this.infoWindow.open(this.map, marker);
    },

    renderMarkers() {
      if (!this.map || !window.google || !window.google.maps) {
        return;
      }
      this.clearGoogleMarkers();

      const { google } = window;
      const matchedColor = "#00897B";
      const unmatchedColor = "#E53935";
      const points = this.visibleMarkerList();
      if (!points.length) {
        return;
      }

      let index = 0;
      const attachClusterer = () => {
        this.markerClusterer = new MarkerClusterer({
          map: this.map,
          markers: this.gMarkers,
          algorithm: new SuperClusterViewportAlgorithm({
            maxZoom: 19,
            radius: 72,
            viewportPadding: 80,
          }),
          renderer: inventoryClusterRenderer,
        });
      };

      const buildStep = () => {
        const end = Math.min(index + MARKER_BUILD_CHUNK, points.length);
        for (; index < end; index += 1) {
          const point = points[index];
          const matched = point.isInventoryMatched;
          const marker = new google.maps.Marker({
            position: { lat: point.lat, lng: point.lng },
            title: `${point.brand || "Submission"} — ${matched ? "Matched" : "Unmatched"}`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: matched ? matchedColor : unmatchedColor,
              fillOpacity: 0.92,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              scale: 6,
            },
            optimized: true,
          });
          marker.set(META_KEY, point);
          marker.addListener("click", () => this.onInventoryMarkerClick(marker));
          this.gMarkers.push(marker);
        }
        if (index < points.length) {
          this.markerBuildRafId = requestAnimationFrame(buildStep);
        } else {
          this.markerBuildRafId = null;
          attachClusterer();
        }
      };

      this.markerBuildRafId = requestAnimationFrame(buildStep);
    },

    fitVisibleMarkers() {
      const list = this.visibleMarkerList();
      if (!this.map || !list.length) {
        return;
      }
      const { google } = window;
      const bounds = new google.maps.LatLngBounds();
      list.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      if (list.length === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(15);
        return;
      }
      this.map.fitBounds(bounds, 72);
    },
  },
};
</script>

<style scoped>
.map-canvas {
  height: 560px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.kpi-tile {
  padding: 10px 12px;
  height: 100%;
}

.kpi-tile--matched {
  border-color: #b2dfdb !important;
  background: linear-gradient(180deg, #e0f2f1 0%, #fff 100%);
}

.kpi-tile--unmatched {
  border-color: #ffcdd2 !important;
  background: linear-gradient(180deg, #ffebee 0%, #fff 100%);
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #cfd8dc;
}

.legend-dot--matched {
  background: #00897b;
}

.legend-dot--unmatched {
  background: #e53935;
}

.legend-pie {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  vertical-align: -2px;
  box-shadow: 0 0 0 1px #cfd8dc;
  background: conic-gradient(#00897b 0deg 200deg, #e53935 200deg 360deg);
}
</style>
